import React, { useState, useEffect } from 'react';
import { Button, Form, Modal, Tabs, Tab } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { MdDone, MdErrorOutline } from 'react-icons/md';
import style from './AddCategoryPopup.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { addCategory, updateCategoryById } from '../../../store/slices/category/categoryThunk';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { clearSelectedCategory, updateCategory } from '../../../store/slices/category/categorySlice';
const EMPTY_TRANSLATIONS = {
  en: { name: '', description: '' },
  ka: { name: '', description: '' }
};

export function AddCategoryPopup({ handleClose, show, fetchCategory }) {
  const { t } = useTranslation();
  const [lang, setLang] = useState('en');
  const globalMenu = useSelector((state) => state.globalMenu);
  const category = useSelector((state) => state.category);
  const dispatch = useDispatch();
  const currentCategory = category.currentCategory?.translationsMap;
  const isEditMode = Boolean(category.currentCategory?.id);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const segments = [
    { code: 'en', name: t('common.english') },
    { code: 'ka', name: t('common.georgian') }
    // { code: 'ru', name: t('common.russian') }
  ];
  const {
    register,
    handleSubmit,
    reset,
    watch,
    trigger, // Added trigger to manually run validation if needed
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      translations: EMPTY_TRANSLATIONS
    }
  });

    const hasTabError = (l) => !!errors.translations?.[l];

  const formValues = watch('translations');
  const onSubmit = async (data) => {
    try {
      if (globalMenu.currentGlobalMenu) {
        const payload = {
          translations: Object.entries(data.translations).map(([languageCode, value]) => ({
            languageCode,
            name: value.name,
            description: value.description
          }))
        };
        if (isEditMode) {
          await dispatch(
            updateCategoryById({
              id: category.currentCategory?.id || 0,
              ...payload
            })
          ).unwrap();
          const updatedPayload = {
            ...category.currentCategory,
            translations: payload,
            translationsMap: Array.isArray(payload.translations)
              ? payload.translations.reduce((acc, ts) => {
                  if (ts && ts.languageCode) {
                    acc[ts.languageCode] = {
                      name: ts.name ?? '',
                      description: ts.description ?? ''
                    };
                  }
                  return acc;
                }, {})
              : {}
          };
          dispatch(updateCategory(updatedPayload));
        } else {
          await dispatch(
            addCategory({
              menuTemplateId: globalMenu.currentGlobalMenu.id,
              ...payload
            })
          ).unwrap();
          fetchCategory();
        }
        handleClosePopup();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleClosePopup = () => {
    reset({ translations: EMPTY_TRANSLATIONS });
    dispatch(clearSelectedCategory());
    setLang('en');
    handleClose();
  };

  // Improved helper logic
  const hasError = (l) => !!errors.translations?.[l]?.name || !!errors.translations?.[l]?.description;

  // A tab is complete ONLY if it has text AND no validation errors
  const isComplete = (l) => {
    const name = formValues[l]?.name?.trim();
    const desc = formValues[l]?.description?.trim();
    return name && desc && !hasError(l);
  };

  useEffect(() => {
    if (isEditMode) {
      reset({ translations: currentCategory });
    } else {
      reset({ translations: EMPTY_TRANSLATIONS });
    }
  }, [isEditMode, currentCategory, reset]);

  return (
    <Modal show={show} onHide={handleClosePopup} centered size="lg" backdrop="static">
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="fw-bold">
          {t(isEditMode ? 'menu_management.category.updateCategory' : 'menu_management.category.addCategory')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* <Tabs activeKey={lang} onSelect={(k) => setLang(k)} className={`mb-4 ${style.customTabs}`} transition={false}>
          {['en', 'ka', 'ru'].map((l) => (
            <Tab
              key={l}
              eventKey={l}
              title={
                <span className="d-flex align-items-center gap-2">
                  {l === 'en' ? 'English' : l === 'ka' ? 'Georgian' : 'Russian'}
                  
                  {hasError(l) ? (
                    <MdErrorOutline size={18} className="text-danger" />
                  ) : (
                    isComplete(l) && <MdDone size={18} className="text-success" />
                  )}
                </span>
              }
            />
          ))}
        </Tabs> */}

        <div className="w-100 mb-4">
          <div className={style.segmentControl}>
            {segments.map((segment, index) => (
              <button
                key={segment.code}
                type="button"
                onClick={() => {
                  setSelectedTabIndex(index);
                  setLang(segment.code);
                }}
                className={`${style.segmentBtn} ${selectedTabIndex === index ? style.active : ''}`}
              >
                <div className="d-flex align-items-center justify-content-center gap-2">
                  {segment.name}
                  {hasTabError(segment.code) ? (
                    <MdErrorOutline size={18} className="text-danger" />
                  ) : (
                    isComplete(segment.code) && <MdDone size={18} className="text-success" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className={style.tabContentWrapper}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            {['en', 'ka'].map((l) => (
              <div key={l} className={lang === l ? style.tabFadeIn : 'd-none'}>
                {/* Selected Menu Items */}

                <Form.Group className="mb-4">
                  <Form.Label className="small fw-bold text-muted text-uppercase">
                    {t('menu_management.category.selectedMenu')} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    className={`py-2 ${errors.translations?.[l]?.name ? 'is-invalid' : ''}`}
                    type="text"
                    value={globalMenu.currentGlobalMenu && globalMenu.currentGlobalMenu?.name}
                    disabled
                  />
                </Form.Group>
                {/* NAME FIELD */}
                <Form.Group className="mb-4">
                  <Form.Label className="small fw-bold text-muted text-uppercase">
                    {t('menu_management.category.categoryName')} ({l}) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    className={`py-2 ${errors.translations?.[l]?.name ? 'is-invalid' : ''}`}
                    type="text"
                    placeholder={`${t('menu_management.category.namePlaceholder')}`}
                    {...register(`translations.${l}.name`, {
                      required: t('validation.categoryNameRequired')
                    })}
                  />
                </Form.Group>

                {/* DESCRIPTION FIELD */}
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold text-muted text-uppercase">
                    {t('menu_management.category.categoryDescription')} ({l}) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    className={`py-2 ${errors.translations?.[l]?.description ? 'is-invalid' : ''}`}
                    as="textarea"
                    rows={4}
                    placeholder={`${t('menu_management.category.descriptionPlaceholder')}`}
                    {...register(`translations.${l}.description`, {
                      required: t('validation.categoryDescriptionRequired')
                    })}
                  />
                </Form.Group>

                {hasError(l) && <p className="text-danger small mt-2">{t('validation.pleaseFillBoth')}</p>}
              </div>
            ))}
          </Form>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button variant="link" className="text-muted text-decoration-none" onClick={handleClosePopup}>
          Cancel
        </Button>
        <Button variant="dark" className="px-4 py-2 shadow-sm" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          {isSubmitting ? t('common.saving') : t(isEditMode ? 'common.update' : 'common.add')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
